// ════════════════════════════════════════════════════════════════
// Access Control — Rule Definitions
// Declarative, data-driven. Extend by adding entries.
// ════════════════════════════════════════════════════════════════

import type { FeatureKey, FeatureRule, ActionKey, ActionRule, ContentType, ContentRule } from './types';

// ── Feature Rules ────────────────────────────────────────────
export const FEATURE_RULES: Record<FeatureKey, FeatureRule> = {
  // ── Closed Beta: Beta-only features ────────────────────────
  dive_guide:             { minTier: 'free', betaOnly: true },
  visibility_reports:     { minTier: 'free', betaOnly: true },
  pois:                   { minTier: 'free', betaOnly: true },
  map:                    { minTier: 'free', betaOnly: true },

  // ── Community: in Closed Beta freigegeben ──────────────────
  buddyhub:               { minTier: 'free', betaOnly: true },
  community:              { minTier: 'free', betaOnly: true },

  // ── Visibility-Datenzugriff (Closed Beta + Visibility Full Beta) ──
  visibility_history_full: { minTier: 'free', betaOnly: true },
  visibility_counts_full:  { minTier: 'free', betaOnly: true },

  // ── Hidden / admin-only in Closed Beta ─────────────────────
  rewards_backend:        { minTier: 'guest' }, // System-Flag, immer "auf"
  rewards_ui:             { minTier: 'free', allowedRoles: ['admin', 'super_admin'] },
  newsletter:             { minTier: 'free', allowedRoles: ['admin', 'super_admin'] },
  ai_dive_planner:        { minTier: 'premium', allowedRoles: ['admin', 'super_admin'] },

  // ── Deactivated in Closed Beta ─────────────────────────────
  alerts:                 { minTier: 'free' },
  public_community:       { minTier: 'guest' },
  trip_planning:          { minTier: 'premium', entitlementOverride: 'can_access_trip_planning' },
  advanced_analytics:     { minTier: 'premium', entitlementOverride: 'can_view_advanced_analytics' },

  // ── Role-restricted ────────────────────────────────────────
  partner_platform:       { minTier: 'free', allowedRoles: ['partner', 'org_admin', 'admin', 'super_admin'] },
  verification_workflow:  { minTier: 'free', allowedRoles: ['admin', 'super_admin'] },
  admin:                  { minTier: 'free', allowedRoles: ['admin', 'super_admin', 'spot_admin', 'verifier', 'moderator', 'org_admin'] },
  beta_center:            { minTier: 'free', allowedRoles: ['admin', 'super_admin'], entitlementOverride: 'can_access_beta_features' },
  beta_program_management:{ minTier: 'free', allowedRoles: ['admin', 'super_admin'] },

  // ── Public features ────────────────────────────────────────
  landing_page:           { minTier: 'guest' },
  legal_pages:            { minTier: 'guest' },
  login:                  { minTier: 'guest' },
  registration:           { minTier: 'guest' },
  beta_apply:             { minTier: 'guest' },
  beta_invite_redeem:     { minTier: 'guest' },
};

// ── Action Rules ─────────────────────────────────────────────
export const ACTION_RULES: Record<ActionKey, ActionRule> = {
  // Visibility reports
  'visibility_report.create':       { feature: 'visibility_reports', minTier: 'free' },
  'visibility_report.edit':         { feature: 'visibility_reports', minTier: 'free', ownerAllowed: true },
  'visibility_report.delete':       { feature: 'visibility_reports', minTier: 'free', ownerAllowed: true, allowedRoles: ['admin'] },
  'visibility_report.view_history': { feature: 'visibility_reports', minTier: 'free', entitlementOverride: 'can_view_full_visibility_history' },

  // Spots
  'spot.submit':        { feature: 'dive_guide', minTier: 'free' },
  'spot.edit':          { feature: 'dive_guide', minTier: 'free', allowedRoles: ['spot_admin', 'admin'] },
  'spot.verify':        { feature: 'verification_workflow', minTier: 'free', allowedRoles: ['verifier', 'admin'] },
  'spot.delete':        { feature: 'dive_guide', minTier: 'free', allowedRoles: ['admin'] },
  'spot.report_issue':  { feature: 'dive_guide', minTier: 'free' },
  'spot.submit_warning':{ feature: 'dive_guide', minTier: 'free' },

  // POIs
  'poi.submit':  { feature: 'pois', minTier: 'free' },
  'poi.claim':   { feature: 'pois', minTier: 'free' },
  'poi.edit':    { feature: 'pois', minTier: 'free', allowedRoles: ['spot_admin', 'admin'], ownerAllowed: true },

  // Favorites
  'favorites.save':         { feature: 'dive_guide', minTier: 'free' },
  'favorites.manage_lists': { feature: 'dive_guide', minTier: 'free' },

  // Alerts
  'alert.create': { feature: 'alerts', minTier: 'free' },
  'alert.edit':   { feature: 'alerts', minTier: 'free', ownerAllowed: true },
  'alert.delete': { feature: 'alerts', minTier: 'free', ownerAllowed: true },

  // Images
  'image.upload': { feature: 'dive_guide', minTier: 'free' },
  'image.delete': { feature: 'dive_guide', minTier: 'free', ownerAllowed: true, allowedRoles: ['admin'] },

  // Community
  'buddy.request': { feature: 'buddyhub', minTier: 'free' },
  'buddy.manage':  { feature: 'buddyhub', minTier: 'free' },

  // User management
  'user.verify':      { feature: 'admin', minTier: 'free', allowedRoles: ['admin', 'moderator', 'local_guide'] },
  'user.suspend':     { feature: 'admin', minTier: 'free', allowedRoles: ['admin'] },
  'user.impersonate': { feature: 'admin', minTier: 'free', allowedRoles: ['super_admin'] },

  // Admin
  'admin.access':                { feature: 'admin', minTier: 'free', allowedRoles: ['admin', 'spot_admin', 'verifier', 'org_admin', 'moderator', 'local_guide', 'partner'] },
  'admin.manage_users':          { feature: 'admin', minTier: 'free', allowedRoles: ['admin'] },
  'admin.manage_settings':       { feature: 'admin', minTier: 'free', allowedRoles: ['admin'] },
  'admin.import_data':           { feature: 'admin', minTier: 'free', allowedRoles: ['admin', 'spot_admin'] },
  'admin.manage_organisations':  { feature: 'admin', minTier: 'free', allowedRoles: ['admin', 'org_admin'] },
  'admin.manage_subscriptions':  { feature: 'admin', minTier: 'free', allowedRoles: ['admin'] },
  'admin.preview_access':        { feature: 'admin', minTier: 'free', allowedRoles: ['admin', 'super_admin'] },

  // Beta
  'beta_program.manage': { feature: 'beta_center', minTier: 'free', allowedRoles: ['admin'] },
  'beta_program.join':   { feature: 'beta_center', minTier: 'free' },

  // Moderation
  'moderation.review_spots':   { feature: 'admin', minTier: 'free', allowedRoles: ['admin', 'spot_admin', 'moderator'] },
  'moderation.review_reports': { feature: 'admin', minTier: 'free', allowedRoles: ['admin', 'spot_admin', 'moderator'] },

  // Partner
  'partner.manage_content': { feature: 'partner_platform', minTier: 'free', allowedRoles: ['org_admin', 'admin', 'partner'], ownerAllowed: true },
};

// ── Content Rules ────────────────────────────────────────────
export const CONTENT_RULES: Record<ContentType, ContentRule> = {
  spot:              { feature: 'dive_guide', minTier: 'guest' },
  visibility_report: { feature: 'visibility_reports', minTier: 'guest' },
  poi:               { feature: 'pois', minTier: 'guest' },
  guide:             { feature: 'dive_guide', minTier: 'guest' },
  image:             { feature: 'dive_guide', minTier: 'guest' },
  article:           { feature: 'dive_guide', minTier: 'guest' },
  alert:             { feature: 'alerts', minTier: 'free', ownerAllowed: true },
  buddy_profile:     { feature: 'buddyhub', minTier: 'free' },
};

// ── Gate Messages (de/en) ────────────────────────────────────
export const AUTH_MESSAGES: Record<string, { de: string; en: string }> = {
  dive_guide:          { de: 'Melde dich an, um alle Funktionen des Dive Guides zu nutzen.', en: 'Sign in to use all Dive Guide features.' },
  visibility_reports:  { de: 'Melde dich an, um Conditions mit der Community zu teilen.', en: 'Sign in to share conditions with the community.' },
  buddyhub:            { de: 'Melde dich an, um das BuddyHub zu nutzen.', en: 'Sign in to use BuddyHub.' },
  alerts:              { de: 'Melde dich an, um Benachrichtigungen zu erhalten.', en: 'Sign in to receive notifications.' },
  favorites:           { de: 'Melde dich an, um Lieblingsspots zu speichern.', en: 'Sign in to save favorite spots.' },
  default:             { de: 'Melde dich an, um diese Funktion zu nutzen.', en: 'Sign in to use this feature.' },
};

export const PREMIUM_MESSAGES: Record<string, { de: string; en: string }> = {
  trip_planning:        { de: 'Die Tagesplanung ist Teil des Premium-Plans.', en: 'Trip planning is part of the Premium plan.' },
  advanced_analytics:   { de: 'Erweiterte Analysen sind Teil des Premium-Plans.', en: 'Advanced analytics are part of the Premium plan.' },
  default:              { de: 'Diese Funktion ist Teil des Premium-Plans.', en: 'This feature is part of the Premium plan.' },
};
